/**
 * Brand Kit Type Definitions
 * Based on AdCP Brand Protocol schema: https://adcontextprotocol.org/schemas/v1/brand.json
 *
 * API: /api/v1/ai/brand-kits
 */

export interface LocalizedName {
  [languageCode: string]: string
}

export type KellerType = 'master' | 'sub_brand' | 'endorsed' | 'independent'

export type HouseArchitecture = 'branded_house' | 'house_of_brands' | 'hybrid'

export type BrandKitStatus = 'active' | 'draft' | 'archived'

export interface BrandLogo {
  url: string
  orientation?: 'square' | 'horizontal' | 'vertical' | 'stacked'
  background?: 'dark-bg' | 'light-bg' | 'transparent-bg'
  variant?: 'primary' | 'secondary' | 'icon' | 'wordmark' | 'full-lockup'
  tags?: string[]
  usage?: string
  width?: number
  height?: number
}

export interface BrandColors {
  primary?: string | string[]
  secondary?: string | string[]
  accent?: string | string[]
  background?: string | string[]
  text?: string | string[]
}

export interface BrandFonts {
  primary?: string
  secondary?: string
  font_urls?: string[]
}

export interface BrandTone {
  voice?: string
  style?: string
  attributes?: string[]
  dos?: string[]
  donts?: string[]
}

export interface BrandProperty {
  type: 'website' | 'mobile_app' | 'ctv_app' | 'desktop_app' | 'dooh' | 'podcast' | 'radio' | 'streaming_audio'
  identifier: string
  store?: 'apple' | 'google' | 'amazon' | 'roku' | 'samsung' | 'lg' | 'other'
  region?: string
  primary?: boolean
}

export interface ProductCatalog {
  feed_url: string
  feed_format?: 'google_merchant_center' | 'facebook_catalog' | 'openai_product_feed' | 'custom'
  categories?: string[]
  last_updated?: string
  update_frequency?: 'realtime' | 'hourly' | 'daily' | 'weekly'
}

export interface BrandDisclaimer {
  text: string
  context?: string
  required?: boolean
}

export interface BrandTrademark {
  registry: string
  number: string
  mark: string
}

export interface BrandContact {
  name?: string
  email?: string
  phone?: string
  domain?: string
}

export interface BrandAgent {
  url: string
  id: string
}

export interface PhotographyGuidelines {
  style?: string
  subjects?: string[]
  forbidden?: string[]
}

export interface GraphicStyleGuidelines {
  approach?: string
  motion_guidelines?: string
}

export interface ColorwayGuidelines {
  primary_palette?: string[]
  accent_palette?: string[]
  usage?: string
}

export interface TypeScaleGuidelines {
  display?: string
  headline?: string
  body?: string
  fallback?: string
}

export interface MotionGuidelines {
  easing?: string
  signature_elements?: string[]
  duration_guidelines?: string
}

export interface VisualGuidelines {
  photography?: PhotographyGuidelines
  graphic_style?: GraphicStyleGuidelines
  colorways?: ColorwayGuidelines
  type_scale?: TypeScaleGuidelines
  motion?: MotionGuidelines
}

export interface VoiceSynthesis {
  provider?: string
  voice_id?: string
  settings?: {
    stability?: number
    similarity_boost?: number
    style?: number
    use_speaker_boost?: boolean
    tone_descriptors?: string[]
    gender?: string
    age_range?: string
    accent?: string
    [key: string]: any
  }
}

export interface BrandRights {
  available_uses?: string[]
  countries?: string[]
  excluded_countries?: string[]
  exclusivity_model?: string
  content_restrictions?: string[]
}

export interface BrandContext {
  schema_version?: string
  generated_at?: string
  authorization_level?: string
  locale?: string
}

export interface BrandAsset {
  asset_id: string
  asset_type: string
  url: string
  tags?: string[]
  name?: string
  description?: string
  width?: number
  height?: number
  duration_seconds?: number
  file_size_bytes?: number
  format?: string
}

export interface AuthorizedOperator {
  domain: string
  brands: string[]
  countries?: string[]
}

export interface BrandHouse {
  domain: string
  name: string
  architecture?: HouseArchitecture
}

export interface Brand {
  id: string
  url?: string
  names: LocalizedName[]
  keller_type?: KellerType
  parent_brand?: string
  description?: string
  industry?: string
  target_audience?: string
  tagline?: string
  logos?: BrandLogo[]
  colors?: BrandColors
  fonts?: BrandFonts
  visual_guidelines?: VisualGuidelines
  tone?: BrandTone | string
  voice_synthesis?: VoiceSynthesis
  properties?: BrandProperty[]
  product_catalog?: ProductCatalog
  privacy_policy_url?: string
  disclaimers?: BrandDisclaimer[]
  assets?: BrandAsset[]
  rights?: BrandRights
  context?: BrandContext
  avatar?: { provider?: string; avatar_id?: string; settings?: Record<string, any> }
  brand_agent?: BrandAgent
  contact?: BrandContact
}

/**
 * Brand data as returned by the AI extraction or stored in brand_data field.
 * Freeform JSON following ACP brand.json schema.
 */
export interface BrandData {
  [key: string]: any
  id?: string
  names?: LocalizedName[]
  description?: string
  industry?: string
  target_audience?: string
  tone?: BrandTone | string
  colors?: BrandColors
  fonts?: BrandFonts
  logos?: BrandLogo[]
  visual_guidelines?: VisualGuidelines
  voice_synthesis?: VoiceSynthesis
  disclaimers?: (string | BrandDisclaimer)[]
  tagline?: string
  house?: BrandHouse
  brands?: Brand[]
  contact?: BrandContact
  authorized_operators?: AuthorizedOperator[]
  trademarks?: BrandTrademark[]
  assets?: BrandAsset[]
  rights?: BrandRights
  context?: BrandContext
}

/**
 * Top-level BrandKit entity from /api/v1/ai/brand-kits.
 *
 * - List responses omit `brand_data` for performance.
 * - Show/create/update/extract include `brand_data`.
 */
export interface BrandKitAdvertiser {
  id: number
  name: string
}

export interface BrandKit {
  id: number
  name: string
  status: BrandKitStatus
  advertiser_ids: string[]
  advertisers?: BrandKitAdvertiser[]
  agency_ids: string[]
  brand_data?: BrandData
  created_at: string
  updated_at: string
}

/**
 * Form data shape for creating/editing a BrandKit.
 * Kept separate from API shape for form convenience.
 */
export interface BrandKitFormData {
  name: string
  house: BrandHouse
  brands: Brand[]
  contact?: BrandContact
  authorized_operators?: AuthorizedOperator[]
  trademarks?: BrandTrademark[]
  advertiser_ids?: number[]
  agency_ids?: number[]
}

/**
 * Convert form data to the API brand_data payload
 */
export function formDataToBrandData(formData: BrandKitFormData): BrandData {
  return {
    house: formData.house,
    brands: formData.brands,
    contact: formData.contact,
    authorized_operators: formData.authorized_operators,
    trademarks: formData.trademarks,
  }
}

export function createEmptyVisualGuidelines(): VisualGuidelines {
  return {
    photography: { style: '', subjects: [], forbidden: [] },
    graphic_style: { approach: '', motion_guidelines: '' },
    colorways: { primary_palette: [], accent_palette: [], usage: '' },
    type_scale: { display: '', headline: '', body: '', fallback: '' },
    motion: { easing: '', signature_elements: [], duration_guidelines: '' },
  }
}

export function createEmptyRights(): BrandRights {
  return {
    available_uses: [],
    countries: [],
    excluded_countries: [],
    exclusivity_model: 'non-exclusive',
    content_restrictions: [],
  }
}

export function createEmptyVoiceSynthesis(): VoiceSynthesis {
  return { provider: '', voice_id: '', settings: {} }
}

/**
 * Convert API brand_data back to form-friendly structure.
 * Handles both manual (structured) and extracted (flat) brand_data.
 */
export function brandDataToFormData(brandKit: BrandKit): BrandKitFormData {
  const data = brandKit.brand_data || {}

  if (data.house && data.brands) {
    return {
      name: brandKit.name,
      house: data.house,
      brands: data.brands,
      contact: data.contact,
      authorized_operators: data.authorized_operators,
      trademarks: data.trademarks,
      advertiser_ids: brandKit.advertiser_ids?.map(Number).filter(n => !isNaN(n)),
      agency_ids: brandKit.agency_ids?.map(Number).filter(n => !isNaN(n)),
    }
  }

  // Flat brand_data from AI extraction / new schema – wrap into form structure
  const brand: Brand = {
    id: data.brand_id || data.id || '',
    names: data.names || [{ en: brandKit.name || '' }],
    description: data.description || '',
    industry: data.industry || '',
    target_audience: data.target_audience || '',
    tagline: data.tagline || '',
    keller_type: data.keller_type || 'master',
    logos: data.logos || [],
    colors: data.colors || { primary: '#000000', secondary: '#666666' },
    fonts: data.fonts || { primary: '', secondary: '' },
    visual_guidelines: data.visual_guidelines,
    tone: data.tone || { voice: '', attributes: [], dos: [], donts: [] },
    voice_synthesis: data.voice_synthesis,
    properties: data.properties || [],
    disclaimers: normalizeDisclaimers(data.disclaimers),
    product_catalog: data.product_catalog,
    privacy_policy_url: data.privacy_policy_url || '',
    assets: data.assets || [],
    rights: data.rights,
    context: data.context,
  }

  return {
    name: brandKit.name,
    house: data.house || { domain: '', name: '', architecture: 'branded_house' },
    brands: [brand],
    contact: data.contact,
    authorized_operators: data.authorized_operators,
    trademarks: data.trademarks,
    advertiser_ids: brandKit.advertiser_ids?.map(Number).filter(n => !isNaN(n)),
    agency_ids: brandKit.agency_ids?.map(Number).filter(n => !isNaN(n)),
  }
}

function normalizeDisclaimers(disclaimers?: (string | BrandDisclaimer)[]): BrandDisclaimer[] {
  if (!disclaimers) return []
  return disclaimers.map(d =>
    typeof d === 'string' ? { text: d, required: true } : d
  )
}

/**
 * Empty brand template for initializing new brand entries
 */
export function createEmptyBrand(): Brand {
  return {
    id: '',
    names: [{ en: '' }],
    keller_type: 'master',
    description: '',
    industry: '',
    target_audience: '',
    tagline: '',
    logos: [],
    colors: { primary: '#000000', secondary: '#666666' },
    fonts: { primary: '', secondary: '' },
    visual_guidelines: createEmptyVisualGuidelines(),
    tone: { voice: '', attributes: [], dos: [], donts: [] },
    voice_synthesis: createEmptyVoiceSynthesis(),
    properties: [],
    assets: [],
    rights: createEmptyRights(),
  }
}

export function createEmptyBrandKitFormData(): BrandKitFormData {
  return {
    name: '',
    house: { domain: '', name: '', architecture: 'branded_house' },
    brands: [createEmptyBrand()],
    contact: { name: '', email: '' },
    advertiser_ids: [],
    agency_ids: [],
  }
}
